import { filterClasses } from "@swivl-mono/helpers";
import { NotificationType } from "@swivl-mono/types";
import { motion, useAnimationControls } from "motion/react";
import { type CSSProperties, useCallback, useEffect, useRef } from "react";

import "./Toast.scss";
import { getToastClassNamesConfig } from "./ToastClassNamesConfig";
import { ToastEscalation } from "./ToastEscalation";
import { ToastGeneric } from "./ToastGeneric";
import { ToastGroupTicket } from "./ToastGroupTicket";
import { ToastData } from "./ToastRoot";

export interface BaseToastProps {
  style?: CSSProperties;
  data: ToastData;
}

export enum ToastVariant {
  DEFAULT = "default",
  ERROR = "error",
  SUCCESS = "success"
}

export function ToastWrapper(props: BaseToastProps) {
  const { style, data } = props;
  const controls = useAnimationControls();
  const hasExited = useRef(false);

  const toastInternalClassNames = data.escalation
    ? getToastClassNamesConfig(NotificationType.ESCALATION, data.escalation.channel)
    : getToastClassNamesConfig(NotificationType.TOAST, ToastVariant.DEFAULT);

  // Taller card layout for toasts that opt into the X-close treatment.
  const isIconDismiss = data.notification?.type === NotificationType.TOAST && data.notification.data?.iconDismiss === true;

  const animateOut = useCallback(async (): Promise<void> => {
    if (hasExited.current) return;
    hasExited.current = true;
    await controls.start({ opacity: 0, x: 200, transition: { duration: 0.3, ease: "easeInOut" } });
  }, [controls]);

  useEffect(() => {
    controls.start({ opacity: 1, x: 0, transition: { duration: 0.3, ease: "easeInOut" } });
  }, [controls]);

  // Trigger exit animation when the toast is marked as exiting (auto-dismissed by the model)
  useEffect(() => {
    if (data.isExiting) {
      void animateOut();
    }
  }, [data.isExiting, animateOut]);

  const ToastContents = () => {
    if (data.escalation) {
      return <ToastEscalation escalation={data.escalation} onAnimateOut={animateOut} />;
    } else if (data.groupTicketAlert) {
      return <ToastGroupTicket alert={data.groupTicketAlert} onAnimateOut={animateOut} />;
    } else if (data.notification) {
      return <ToastGeneric notification={data.notification} onAnimateOut={animateOut} />;
    }
  };

  return (
    <motion.div
      layout
      className={filterClasses(
        `${toastInternalClassNames.wrapper}`,
        // Lets children respond to the toast's own width, not the viewport.
        "@container flex w-full rounded-lg border-2 p-4 shadow-lg",
        isIconDismiss ? "min-h-[74px] items-center" : "h-[74px] items-center justify-between",
        // Generic notification toasts (e.g. the duplicate-widget success) cap their
        // width so they don't stretch the full third-of-screen container; escalation
        // toasts keep their existing width.
        !data.escalation && "ml-auto max-w-md"
      )}
      initial={{ opacity: 0, x: 200 }}
      animate={controls}
      transition={{ duration: 0.3, ease: "easeInOut" }}
      style={style}
    >
      <ToastContents />
    </motion.div>
  );
}
